import {
  DEFAULT_LANGUAGE_CODE,
  LOCALE_FALLBACKS,
  LOCALES,
} from "@readai/common/constants/locales";
import logger from "@readai/common/logger";
import type { LanguageCode } from "@readai/common/types";

import { delay } from "../utils";

/** Check if the value is a supported language code */
const isSupportedLanguageCode = (value: string): value is LanguageCode =>
  value in LOCALES;

/** Get the correct locale, a fallback or the default */
export const getLocaleOrFallback = (locale: string): LanguageCode => {
  if (isSupportedLanguageCode(locale)) {
    return locale;
  }

  if (locale in LOCALE_FALLBACKS) {
    return LOCALE_FALLBACKS[locale];
  }

  return DEFAULT_LANGUAGE_CODE;
};

/**
 * Retries catalog dynamic imports, with a fallback. Returns a boolean value
 * indicating whether the dynamic import succeeded after a number of retries
 */
export const retryMessageCatalogImport = async (
  localeToLoad: LanguageCode,
  /** Importer function dynamically imports, loads and activates a given messages catalog */
  importerFn: () => Promise<void>,
  /** Fallback function loads the default messages catalog without a dynamic import */
  fallbackFn: () => void,
  maxRetries = 5,
) => {
  if (localeToLoad === DEFAULT_LANGUAGE_CODE) {
    fallbackFn();
    return true;
  }

  try {
    await importerFn();
    return true;
  } catch (_e) {
    logger.error(`Failed to load ${localeToLoad} locale messages, retrying...`);

    for (let i = 0; i < maxRetries; i++) {
      try {
        await importerFn();
        return true;
      } catch (_e) {
        logger.error(
          `Failed to load ${localeToLoad} locale messages on retry #${i + 1}`,
        );

        await delay(1000 * 2 ** (i - 1));
      }
    }

    fallbackFn();
    return false;
  }
};
